<template>
  <el-tree-select
    v-if="!collapse && visible"
    id="select-scope"
    v-model="selected"
    :data="treeData"
    :props="{label: 'label', children: 'children'}"
    node-key="value"
    :placeholder="$t('scope.select')"
    :loading="loading"
    :disabled="switching"
    :render-after-expand="false"
    check-strictly
    filterable
    style="width: 100%"
    @change="handleChange"
  />
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from 'vue'
import {useI18n} from 'vue-i18n'
import {ElMessage} from 'element-plus'
import Srv from '@/services/GraphQLSrv'
import {useAuthStore} from '@/stores/auth'
import {accessTokenStorage, organizationStorage} from '@/helpers/storage'
import {buildScopeTree, type ScopeNode, type ScopeTreeOption} from '@/helpers/scopeTree'

defineProps<{
  collapse: boolean
}>()

// Пункт «без выбора»: у superadmin — вся республика, у остальных — все их скоупы.
const ALL = '__all__'

const {t, locale} = useI18n()
const authStore = useAuthStore()

const nodes = ref<ScopeNode[]>([])
const unrestricted = ref(false)
const selected = ref<string>(ALL)
const loading = ref(false)
const switching = ref(false)

const visible = computed(() => unrestricted.value || nodes.value.length > 0)

const treeData = computed<ScopeTreeOption[]>(() => {
  const allLabel = unrestricted.value ? t('scope.all') : t('scope.allMine')
  return [{value: ALL, label: allLabel}, ...buildScopeTree(nodes.value, locale.value)]
})

async function fetchTree() {
  loading.value = true
  try {
    const res = await Srv.request('query', 'myScopeTree', [
      'activeScopeUuid',
      'unrestricted',
      {nodes: ['uuid', 'parentUuids', 'nameRu', 'nameKz', 'type', 'orgUuid']},
    ], false)
    if (res.type !== 'success' || !res.data) {
      return
    }
    nodes.value = res.data.nodes ?? []
    unrestricted.value = Boolean(res.data.unrestricted)
    selected.value = res.data.activeScopeUuid ?? ALL
  } catch (error) {
    console.error('Error fetching scopes:', error)
    ElMessage.error(t('scope.fetchError'))
  } finally {
    loading.value = false
  }
}

async function handleChange(scopeUuid: string) {
  switching.value = true
  try {
    const res = await Srv.request('mutation', 'userScopeSwitch', ['accessToken'], false, {
      scopeUuid: scopeUuid === ALL ? null : scopeUuid,
    })
    if (res.type !== 'success' || !res.data?.accessToken) {
      ElMessage.error(t('scope.changeError'))
      return
    }

    accessTokenStorage.set(res.data.accessToken)
    authStore.accessToken = res.data.accessToken
    const node = nodes.value.find(n => n.uuid === scopeUuid)
    if (node?.orgUuid) {
      organizationStorage.set(node.orgUuid)
    }

    ElMessage.success(t('scope.changed'))
    setTimeout(() => window.location.reload(), 300)
  } catch (error) {
    console.error('Error switching scope:', error)
    ElMessage.error(t('scope.changeError'))
  } finally {
    switching.value = false
  }
}

onMounted(fetchTree)
</script>
